import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { AlertCircle, CheckCircle2, Activity, Database, Zap, Clock } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DataStore } from '@/lib/data-store';

interface SystemMetric {
  name: string;
  value: number;
  unit: string;
  status: 'healthy' | 'warning' | 'critical';
  target?: number;
}

interface HealthCheck {
  name: string;
  status: 'pass' | 'fail' | 'warning';
  message: string;
  lastCheck: string;
}

const SystemMonitor = () => {
  const [metrics, setMetrics] = useState<SystemMetric[]>([]);
  const [healthChecks, setHealthChecks] = useState<HealthCheck[]>([]);
  const [performanceHistory, setPerformanceHistory] = useState<any[]>([]);
  const [lastRefresh, setLastRefresh] = useState<string>(new Date().toLocaleTimeString());

  // Calculate system metrics
  useEffect(() => {
    const calculateMetrics = () => {
      const data = DataStore.getData();
      const now = new Date();

      // Storage usage
      const storageKey = 'sap_ngecat_data';
      const storedData = localStorage.getItem(storageKey);
      const storageSize = storedData ? (new Blob([storedData]).size / 1024 / 1024).toFixed(2) : '0';
      const storageUsage = parseInt(storageSize) / 5; // 5MB limit assumption

      // Data records count
      const totalRecords =
        (data.customers?.length || 0) +
        (data.projects?.length || 0) +
        (data.invoices?.length || 0) +
        (data.quotations?.length || 0) +
        (data.costs?.length || 0) +
        (data.reports?.length || 0) +
        (data.siteVisits?.length || 0) +
        (data.projectImages?.length || 0);

      // Activity logs
      const activityKey = 'sap_ngecat_activities';
      const activities = localStorage.getItem(activityKey);
      const activityCount = activities ? JSON.parse(activities).length : 0;

      // Memory estimate (simplified)
      const memoryUsage = Math.min((totalRecords * 0.5) / 100, 100); // Estimate based on records

      // Cache hit rate (simulated)
      const cacheHitRate = 85 + Math.random() * 10;

      const newMetrics: SystemMetric[] = [
        {
          name: 'Storage Usage',
          value: Math.min(parseFloat(storageSize), 5),
          unit: 'MB / 5 MB',
          status: parseFloat(storageSize) > 4 ? 'critical' : parseFloat(storageSize) > 3 ? 'warning' : 'healthy',
          target: 5,
        },
        {
          name: 'Total Records',
          value: totalRecords,
          unit: 'records',
          status: totalRecords > 10000 ? 'warning' : 'healthy',
          target: 10000,
        },
        {
          name: 'Activity Logs',
          value: activityCount,
          unit: '/ 1000',
          status: activityCount > 900 ? 'warning' : 'healthy',
          target: 1000,
        },
        {
          name: 'Memory Usage',
          value: memoryUsage,
          unit: '%',
          status: memoryUsage > 80 ? 'critical' : memoryUsage > 60 ? 'warning' : 'healthy',
          target: 100,
        },
        {
          name: 'Cache Hit Rate',
          value: cacheHitRate,
          unit: '%',
          status: cacheHitRate < 70 ? 'warning' : 'healthy',
          target: 100,
        },
        {
          name: 'Response Time',
          value: 45 + Math.random() * 50,
          unit: 'ms',
          status: Math.random() > 0.7 ? 'warning' : 'healthy',
          target: 100,
        },
      ];

      setMetrics(newMetrics);

      // Update performance history
      setPerformanceHistory((prev) => [
        ...prev,
        {
          time: new Date().toLocaleTimeString(),
          memory: memoryUsage,
          responseTime: 45 + Math.random() * 50,
          storage: parseFloat(storageSize),
        },
      ].slice(-20));

      // Health checks
      const checks: HealthCheck[] = [
        {
          name: 'Database',
          status: data ? 'pass' : 'fail',
          message: data ? 'All systems operational' : 'Database connection failed',
          lastCheck: new Date().toLocaleTimeString(),
        },
        {
          name: 'Storage',
          status: parseFloat(storageSize) < 4.5 ? 'pass' : 'warning',
          message: parseFloat(storageSize) < 4.5 ? 'Storage within limits' : 'Storage approaching limit',
          lastCheck: new Date().toLocaleTimeString(),
        },
        {
          name: 'Authentication',
          status: 'pass',
          message: 'Authentication system operational',
          lastCheck: new Date().toLocaleTimeString(),
        },
        {
          name: 'Activity Logging',
          status: activityCount < 950 ? 'pass' : 'warning',
          message: activityCount < 950 ? 'Logging operational' : 'Activity logs near capacity',
          lastCheck: new Date().toLocaleTimeString(),
        },
      ];

      setHealthChecks(checks);
      setLastRefresh(new Date().toLocaleTimeString());
    };

    calculateMetrics();
    const interval = setInterval(calculateMetrics, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'pass':
        return 'text-green-600';
      case 'warning':
        return 'text-yellow-600';
      case 'critical':
      case 'fail':
        return 'text-red-600';
      default:
        return 'text-slate-600';
    }
  };

  const getStatusBgColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'pass':
        return 'bg-green-50 border-green-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'critical':
      case 'fail':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };

  const getStatusIcon = (status: string) => {
    if (status === 'healthy' || status === 'pass') {
      return <CheckCircle2 size={20} className="text-green-600" />;
    } else if (status === 'warning') {
      return <AlertCircle size={20} className="text-yellow-600" />;
    } else {
      return <AlertCircle size={20} className="text-red-600" />;
    }
  };

  const overallHealth = useMemo(() => {
    const failCount = healthChecks.filter(h => h.status === 'fail').length;
    const warningCount = healthChecks.filter(h => h.status === 'warning').length;

    if (failCount > 0) return { status: 'critical', color: 'text-red-600' };
    if (warningCount > 0) return { status: 'warning', color: 'text-yellow-600' };
    return { status: 'healthy', color: 'text-green-600' };
  }, [healthChecks]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">System Monitor</h1>
          <p className="text-slate-600 mt-1">Real-time system health & performance metrics</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-slate-600">Last refresh: {lastRefresh}</p>
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
            size="sm"
            className="mt-2 gap-2"
          >
            <Zap size={16} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overall Health Status */}
      <Card className="border-2 border-slate-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-2">Overall System Health</p>
              <h2 className={`text-4xl font-bold capitalize ${overallHealth.color}`}>
                {overallHealth.status}
              </h2>
            </div>
            <div className={`text-6xl ${overallHealth.color}`}>
              {overallHealth.status === 'healthy' ? '✓' : '⚠'}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {metrics.map((metric, idx) => (
          <Card key={idx} className={`border ${metric.status === 'critical' ? 'border-red-200' : metric.status === 'warning' ? 'border-yellow-200' : 'border-slate-200'}`}>
            <CardContent className="pt-6">
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <p className="text-sm font-medium text-slate-700">{metric.name}</p>
                  <span className={`text-xs font-semibold px-2 py-1 rounded capitalize ${
                    metric.status === 'healthy' ? 'bg-green-100 text-green-700' :
                    metric.status === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {metric.status}
                  </span>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-2xl font-bold text-slate-900">
                      {metric.value.toFixed(1)}
                    </span>
                    <span className="text-sm text-slate-600">{metric.unit}</span>
                  </div>

                  {metric.target && (
                    <Progress
                      value={(metric.value / metric.target) * 100}
                      className="h-2"
                    />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Performance Chart */}
      {performanceHistory.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity size={20} />
              Performance Trend (Last 20 readings)
            </CardTitle>
            <CardDescription>Real-time system performance monitoring</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Memory Trend */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Memory Usage Trend</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={performanceHistory}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                    <Line
                      type="monotone"
                      dataKey="memory"
                      stroke="#3b82f6"
                      dot={false}
                      strokeWidth={2}
                      name="Memory %"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Response Time Trend */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Response Time Trend</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={performanceHistory}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value) => `${value.toFixed(0)}ms`} />
                    <Line
                      type="monotone"
                      dataKey="responseTime"
                      stroke="#ef4444"
                      dot={false}
                      strokeWidth={2}
                      name="Response Time (ms)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Health Checks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 size={20} />
            System Health Checks
          </CardTitle>
          <CardDescription>Detailed status of all system components</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {healthChecks.map((check, idx) => (
              <div
                key={idx}
                className={`p-4 border rounded-lg ${getStatusBgColor(check.status)}`}
              >
                <div className="flex items-start gap-4">
                  {getStatusIcon(check.status)}
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                      <h4 className="font-medium text-slate-900">{check.name}</h4>
                      <span className={`text-xs font-semibold uppercase ${getStatusColor(check.status)}`}>
                        {check.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 mb-1">{check.message}</p>
                    <p className="text-xs text-slate-500">Last checked: {check.lastCheck}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* System Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database size={20} />
            System Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-600 mb-1">Application Version</p>
              <p className="text-lg font-bold text-slate-900">v1.4</p>
            </div>

            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-600 mb-1">Build Date</p>
              <p className="text-lg font-bold text-slate-900">25 May 2026</p>
            </div>

            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-600 mb-1">Runtime</p>
              <p className="text-lg font-bold text-slate-900">React 18.3</p>
            </div>

            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-600 mb-1">Environment</p>
              <p className="text-lg font-bold text-slate-900">Production</p>
            </div>

            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-600 mb-1">Browser Storage</p>
              <p className="text-lg font-bold text-slate-900">localStorage</p>
            </div>

            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-600 mb-1">Data Persistence</p>
              <p className="text-lg font-bold text-slate-900">Enabled</p>
            </div>

            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-600 mb-1">Auto Backup</p>
              <p className="text-lg font-bold text-slate-900">Enabled</p>
            </div>

            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-600 mb-1">Session Status</p>
              <p className="text-lg font-bold text-green-600">Active</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap size={20} />
            Optimization Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {metrics.filter(m => m.status !== 'healthy').length === 0 ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-700 font-medium">System is running optimally</p>
                <p className="text-sm text-green-600 mt-1">All metrics are within healthy ranges</p>
              </div>
            ) : (
              metrics.filter(m => m.status !== 'healthy').map((metric, idx) => (
                <div key={idx} className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="font-medium text-yellow-900">{metric.name}</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    {metric.status === 'warning'
                      ? `${metric.name} is approaching capacity. Consider archiving old records.`
                      : `${metric.name} is critically high. Immediate action recommended.`}
                  </p>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemMonitor;
